import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import './AddAdminForm.css';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../../utils/apiConfig';

const AddAdminForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    department: '',
    accessibleSections: [],
  });
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editAdminId, setEditAdminId] = useState(null);

  const navigate = useNavigate();

  const sections = [
    { id: 'dashboard', name: 'Dashboard', category: 'core' },
    { id: 'inventory', name: 'Inventory Management', category: 'operations' },
    { id: 'orders', name: 'Order Management', category: 'operations' },
    { id: 'purchase', name: 'Purchase Management', category: 'operations' },
    { id: 'reporting', name: 'Reports & Analytics', category: 'analytics' },
    { id: 'support', name: 'Customer Support', category: 'customer' },
    { id: 'billing', name: 'Billing & Invoicing', category: 'finance' },
    { id: 'customers', name: 'Customer Management', category: 'customer' },
    { id: 'staff_management', name: 'Staff Management', category: 'hr' },
    { id: 'vouchers', name: 'Voucher Management', category: 'finance' },
  ];

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleSectionChange = (sectionId) => {
    setFormData((prev) => {
      const accessibleSections = prev.accessibleSections.includes(sectionId)
        ? prev.accessibleSections.filter((s) => s !== sectionId)
        : [...prev.accessibleSections, sectionId];
      return { ...prev, accessibleSections };
    });
  };

  const handleSelectAll = () => {
    setFormData((prev) => ({
      ...prev,
      accessibleSections: sections.map(section => section.id)
    }));
  };

  const handleSelectNone = () => {
    setFormData((prev) => ({
      ...prev,
      accessibleSections: []
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const fetchAdmins = useCallback(async () => {
    setAdminLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch admins');
      const data = await response.json();
      setAdmins(Array.isArray(data) ? data : []);
    } catch (error) {
      setErrors([{ msg: error.message }]);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccessMessage('');
    setLoading(true);
  
    try {
      const token = localStorage.getItem('token');
      const url = isEditing
        ? `${API_CONFIG.BASE_URL}/api/admins/${editAdminId}`
        : `${API_CONFIG.BASE_URL}/api/admins`;
      const method = isEditing ? 'PUT' : 'POST';
  
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
  
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || result.message);
  
      setSuccessMessage(result.message);
      setIsEditing(false);
      setEditAdminId(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'admin',
        department: '',
        accessibleSections: [],
      });
      fetchAdmins();
    } catch (error) {
      setErrors([{ msg: error.message }]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = (admin) => {
    const adminId = admin.id || admin._id;
    console.log('Editing admin with ID:', adminId);
    
    setIsEditing(true);
    setEditAdminId(adminId);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '', 
      role: admin.role,
      department: admin.department || '',
      accessibleSections: admin.accessibleSections || [],
    });
  };

  const handleDeleteAdmin = (id) => {
  const token = localStorage.getItem('token');
  fetch(`${API_CONFIG.BASE_URL}/api/admins/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(() => {
      fetchAdmins();
    });
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      setDeptLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/departments`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch departments');
        }

        const data = await response.json();
        setDepartments(data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Fetch departments error:', error);
        setErrors((prev) => [...prev, { msg: error.message || 'Failed to load departments' }]);
      } finally {
        setDeptLoading(false);
      }
    };

    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  useEffect(() => {
    return () => {
      // Reset states when component unmounts
      setAdmins([]);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'admin',
        department: '',
        accessibleSections: [],
      });
      setIsEditing(false);
      setEditAdminId(null);
    };
  }, []);

  return (
    <div className="add-admin-form">
      <div className="form-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <h2>{isEditing ? 'Edit Admin' : 'Add New Admin'}</h2>
        <div className="header-buttons">
          <button 
            onClick={fetchAdmins} 
            className="refresh-button"
            disabled={adminLoading}
          >
            {adminLoading ? '🔄 Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {successMessage && <div className="success-message">{successMessage}</div>}

      {errors.length > 0 && (
        <div className="error-messages">
          {errors.map((error, index) => (
            <p key={index}>{error.msg}</p>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="Enter email"
          />
        </div>

        <div className="form-group pass-group">
          <label htmlFor="password">
            Password {isEditing && '(Leave blank to keep current password)'}
          </label>
          <div className="pass-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={formData.password}
              onChange={handleChange}
              required={!isEditing}
              placeholder={isEditing ? 'Enter new password (optional)' : 'Enter password'}
            />
            <button
              type="button"
              className="pass-toggle"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {!isEditing && (
            <div className="pass-strength">
              <div className={`strength-bar ${formData.password.length > 0 ? 'active' : ''}`}></div>
              <div className={`strength-bar ${formData.password.length > 3 ? 'active' : ''}`}></div>
              <div className={`strength-bar ${formData.password.length > 6 ? 'active' : ''}`}></div>
              <div className={`strength-bar ${formData.password.length > 9 ? 'active' : ''}`}></div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="role">Role</label>
          <select id="role" value={formData.role} onChange={handleChange} required>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>

        {formData.role === 'admin' && (
          <div className="form-group">
            <label htmlFor="department">Department</label>
            <select
              id="department"
              value={formData.department}
              onChange={handleChange}
              required
              disabled={deptLoading}
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.id || dept._id} value={dept.name}>
                  {dept.name}
                </option>
              ))}
            </select>
            {deptLoading && <small>Loading departments...</small>}
          </div>
        )}

        <div className="form-group">
          <div className="sections-header">
            <label>Accessible Sections</label>
            <div className="section-controls">
              <button
                type="button"
                className="select-all-btn"
                onClick={handleSelectAll}
              >
                Select All
              </button>
              <button
                type="button"
                className="select-none-btn"
                onClick={handleSelectNone}
              >
                Clear All
              </button>
            </div>
          </div>
          <div className="checkbox-group">
            {sections.map((section) => (
              <label key={section.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.accessibleSections.includes(section.id)}
                  onChange={() => handleSectionChange(section.id)}
                />
                <span>{section.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-buttons">
          <button
            type="submit"
            className="submit-btn"
            disabled={loading || (formData.role === 'admin' && !formData.department)}
          >
            {loading ? (
              <>
                <span className="spinner"></span> {isEditing ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              isEditing ? 'Update Admin' : 'Add Admin'
            )}
          </button>
          
          {isEditing && (
            <button
              type="button"
              className="cancel-btn"
              onClick={() => {
                setIsEditing(false);
                setEditAdminId(null);
                setFormData({
                  name: '',
                  email: '',
                  password: '',
                  role: 'admin',
                  department: '',
                  accessibleSections: [],
                });
                setSuccessMessage('');
                setErrors([]);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="admin-list-section">
        <h3>Existing Admins</h3>
        {adminLoading ? (
          <div className="loading">Loading admins...</div>
        ) : admins.length === 0 ? (
          <div className="no-admins">
            <p>No admins found</p>
            <button onClick={fetchAdmins} className="retry-button">
              🔄 Retry
            </button>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Accessible Sections</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => {
                const adminId = admin.id || admin._id;
                return (
                  <tr key={adminId}>
                    <td data-label="Name">{admin.name}</td>
                    <td data-label="Email">{admin.email}</td>
                    <td data-label="Role">{admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}</td>
                    <td data-label="Department">{admin.department || 'N/A'}</td>
                    <td data-label="Accessible Sections">
                      {admin.accessibleSections && admin.accessibleSections.length > 0 ? (
                        <ul className="section-list">
                          {admin.accessibleSections.map((section, idx) => (
                            <li key={idx}>{section}</li>
                          ))}
                        </ul>
                      ) : (
                        'None'
                      )}
                    </td>
                    <td data-label="Actions" className="actions-cell">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditAdmin(admin)}
                        title="Edit Admin"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteAdmin(adminId)}
                        title="Delete Admin"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AddAdminForm;